import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Bold, Italic, Underline, Link2, Plus, Trash2 } from 'lucide-react';

/* ═══════════════════════════════════════════
   internalRefTargetType values per S1000D issue
   Issue 4.1+ : irtt enumeration (Chap 3.9.5.2.1.2)
   Issue 4.0  : textual NMTOKEN values
   ═══════════════════════════════════════════ */
export const INTERNAL_REF_TYPES_41 = [
  { value: 'irtt01', label: 'Figure' },
  { value: 'irtt02', label: 'Table' },
  { value: 'irtt03', label: 'Multimedia' },
  { value: 'irtt04', label: 'Supply' },
  { value: 'irtt05', label: 'Support equipment' },
  { value: 'irtt06', label: 'Spares' },
  { value: 'irtt07', label: 'Para' },
  { value: 'irtt08', label: 'Step' },
  { value: 'irtt09', label: 'Graphic' },
  { value: 'irtt10', label: 'Multimedia object' },
  { value: 'irtt11', label: 'Hotspot' },
  { value: 'irtt12', label: 'Parameter' },
];
export const INTERNAL_REF_TYPES_40 = [
  { value: 'figure', label: 'Figure' },
  { value: 'table', label: 'Table' },
  { value: 'multimedia', label: 'Multimedia' },
  { value: 'supply', label: 'Supply' },
  { value: 'supequip', label: 'Support equipment' },
  { value: 'spares', label: 'Spares' },
  { value: 'para', label: 'Para' },
  { value: 'step', label: 'Step' },
  { value: 'graphic', label: 'Graphic' },
  { value: 'multimediaobject', label: 'Multimedia object' },
  { value: 'hotspot', label: 'Hotspot' },
  { value: 'param', label: 'Parameter' },
  { value: 'other', label: 'Other' },
];
export function getInternalRefTypes(s1000dIssue) {
  return /4\.0/.test(s1000dIssue || '') ? INTERNAL_REF_TYPES_40 : INTERNAL_REF_TYPES_41;
}

/* ═══════════════════════════════════════════
   rebuildInlineRefs
   Keeps inlineRefs in sync with marker text.
   ═══════════════════════════════════════════ */
export function rebuildInlineRefs(content, existingRefs = []) {
  if (!content) return [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__|\[DMRef:[^\]]*\]|\[PMRef:[^\]]*\]|\[ExtRef:[^\]]*\]|\[Ref:[^\]]*\])/g;
  const markers = content.match(pattern);
  if (!markers) return [];

  const pool = [...(existingRefs || [])];
  const dequeue = (key) => {
    const idx = pool.findIndex(r => {
      const k = r.type === 'emphasis' ? `emphasis_${r.emphasisType}` : r.type;
      return k === key;
    });
    return idx >= 0 ? pool.splice(idx, 1)[0] : null;
  };

  const newRefs = [];
  for (const m of markers) {
    if (m.startsWith('**') && m.endsWith('**')) {
      const existing = dequeue('emphasis_em01');
      newRefs.push(existing ? { ...existing, text: m.slice(2, -2) } : { type: 'emphasis', emphasisType: 'em01', text: m.slice(2, -2) });
    } else if (m.startsWith('__') && m.endsWith('__')) {
      const existing = dequeue('emphasis_em03');
      newRefs.push(existing ? { ...existing, text: m.slice(2, -2) } : { type: 'emphasis', emphasisType: 'em03', text: m.slice(2, -2) });
    } else if (m.startsWith('*') && m.endsWith('*') && !m.startsWith('**')) {
      const existing = dequeue('emphasis_em02');
      newRefs.push(existing ? { ...existing, text: m.slice(1, -1) } : { type: 'emphasis', emphasisType: 'em02', text: m.slice(1, -1) });
    } else if (m.startsWith('[DMRef:')) {
      const existing = dequeue('dmRef');
      newRefs.push(existing || { type: 'dmRef', ref: '', techName: '', infoName: '' });
    } else if (m.startsWith('[PMRef:')) {
      const existing = dequeue('pmRef');
      newRefs.push(existing || { type: 'pmRef', ref: '', title: '' });
    } else if (m.startsWith('[Ref:')) {
      const existing = dequeue('internalRef');
      newRefs.push(existing || { type: 'internalRef', refId: '', targetType: '' });
    } else if (m.startsWith('[ExtRef:')) {
      const existing = dequeue('externalPubRef');
      newRefs.push(existing || { type: 'externalPubRef', title: '', code: '' });
    }
  }
  return newRefs;
}

/* ═══════════════════════════════════════════
   renderInlinePreview — simple formatted preview of markers
   ═══════════════════════════════════════════ */
export function renderInlinePreview(text) {
  if (!text) return null;
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|__[^_]+__|\[DMRef:[^\]]*\]|\[PMRef:[^\]]*\]|\[ExtRef:[^\]]*\]|\[Ref:[^\]]*\])/g;
  const parts = text.split(pattern);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('__') && p.endsWith('__')) return <u key={i}>{p.slice(2, -2)}</u>;
    if (p.startsWith('*') && p.endsWith('*') && !p.startsWith('**')) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith('[DMRef:')) return <span key={i} className="text-violet-600 font-mono text-xs bg-violet-50 px-1 rounded">{p}</span>;
    if (p.startsWith('[PMRef:')) return <span key={i} className="text-blue-600 font-mono text-xs bg-blue-50 px-1 rounded">{p}</span>;
    if (p.startsWith('[ExtRef:')) return <span key={i} className="text-amber-600 font-mono text-xs bg-amber-50 px-1 rounded">{p}</span>;
    if (p.startsWith('[Ref:')) return <span key={i} className="text-teal-600 font-mono text-xs bg-teal-50 px-1 rounded">{p}</span>;
    return <span key={i}>{p}</span>;
  });
}

/* ═══════════════════════════════════════════
   RICH PARA EDITOR
   Reusable component for editing <para> content with:
   - Emphasis toolbar (Bold/Italic/Underline)
   - dmRef, pmRef, externalPubRef insertion
   - randomList / sequentialList inline lists
   - Inline preview strip
   ═══════════════════════════════════════════ */
export const RichParaEditor = ({
  value = '',
  inlineRefs = [],
  inlineList = null,
  onChange,
  dmlEntries = [],
  dirFiles = [],
  internalTargets = [],
  s1000dIssue = '',
  placeholder = 'Enter text... Use toolbar for emphasis and references',
  label = null,
  rows = 2,
  compact = false,
  id = 'rpe',
}) => {
  const paraRef = useRef(null);
  const [showRefInput, setShowRefInput] = useState(null);
  const [refDisplay, setRefDisplay] = useState('');
  const [refCode, setRefCode] = useState('');
  const [refTargetType, setRefTargetType] = useState('');
  const irttOptions = getInternalRefTypes(s1000dIssue);

  const emit = (updates) => {
    onChange({ text: value, inlineRefs, inlineList, ...updates });
  };

  // Emphasis: wrap selected text or insert placeholder
  const wrapSelection = (prefix, suffix) => {
    const ta = paraRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end) || 'text';
    const wrapped = `${prefix}${selected}${suffix}`;
    const newContent = value.substring(0, start) + wrapped + value.substring(end);
    const newRefs = rebuildInlineRefs(newContent, inlineRefs);
    emit({ text: newContent, inlineRefs: newRefs.length > 0 ? newRefs : [] });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(start + wrapped.length, start + wrapped.length); }, 0);
  };

  // Reference insertion
  const doInsertRef = () => {
    if (!showRefInput) return;
    const ta = paraRef.current;
    const pos = ta ? ta.selectionStart : value.length;
    if (showRefInput === 'intRef') {
      if (!refCode) return;
      const marker = `[Ref: ${refCode}]`;
      const newContent = value.substring(0, pos) + marker + value.substring(pos);
      const refData = { type: 'internalRef', refId: refCode, targetType: refTargetType || irttOptions[0].value };
      const newRefs = rebuildInlineRefs(newContent, [...(inlineRefs || []), refData]);
      emit({ text: newContent, inlineRefs: newRefs.length > 0 ? newRefs : [] });
      setShowRefInput(null); setRefDisplay(''); setRefCode(''); setRefTargetType('');
      return;
    }
    const display = refDisplay || (showRefInput === 'dmRef' ? 'Data Module' : showRefInput === 'pmRef' ? 'Publication' : 'External Ref');
    const labelTag = showRefInput === 'dmRef' ? 'DMRef' : showRefInput === 'pmRef' ? 'PMRef' : 'ExtRef';
    const marker = `[${labelTag}: ${display}]`;
    const newContent = value.substring(0, pos) + marker + value.substring(pos);
    let refData;
    if (showRefInput === 'dmRef') refData = { type: 'dmRef', ref: refCode || '', techName: display, infoName: '' };
    else if (showRefInput === 'pmRef') refData = { type: 'pmRef', ref: refCode || '', title: display };
    else refData = { type: 'externalPubRef', title: display, code: refCode || '' };
    const existingRefs = [...(inlineRefs || []), refData];
    const newRefs = rebuildInlineRefs(newContent, existingRefs);
    emit({ text: newContent, inlineRefs: newRefs.length > 0 ? newRefs : [] });
    setShowRefInput(null); setRefDisplay(''); setRefCode('');
  };

  // Text change
  const handleChange = (e) => {
    const newContent = e.target.value;
    const newRefs = rebuildInlineRefs(newContent, inlineRefs);
    emit({ text: newContent, inlineRefs: newRefs.length > 0 ? newRefs : [] });
  };

  // List management
  const hasInlineList = !!inlineList;
  const listType = inlineList?.type || 'randomList';
  const listItems = inlineList?.items || [];

  const addInlineList = (type) => emit({ inlineList: { type, items: [''] } });
  const removeInlineList = () => emit({ inlineList: null });
  const updateListItem = (i, val) => {
    const items = [...listItems]; items[i] = val;
    emit({ inlineList: { ...inlineList, items } });
  };
  const removeListItem = (i) => {
    const items = listItems.filter((_, idx) => idx !== i);
    emit({ inlineList: items.length > 0 ? { ...inlineList, items } : null });
  };
  const addListItem = () => emit({ inlineList: { ...inlineList, items: [...listItems, ''] } });

  const toolbarSize = compact ? 'text-[9px]' : 'text-[10px]';
  const iconSize = compact ? 'w-3 h-3' : 'w-3.5 h-3.5';

  return (
    <div className="space-y-1.5" data-testid={`rich-para-${id}`}>
      {label && <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">{label}</span>}

      {/* Toolbar */}
      <div className="flex items-center gap-0.5 border border-slate-200 rounded px-1 py-0.5 bg-white flex-wrap" data-testid={`rpe-toolbar-${id}`}>
        <button onClick={() => wrapSelection('**', '**')} className={`px-1 py-0.5 rounded font-bold text-slate-600 hover:bg-slate-100 ${toolbarSize}`} title="Bold (em01)">
          <Bold className={iconSize} />
        </button>
        <button onClick={() => wrapSelection('*', '*')} className={`px-1 py-0.5 rounded italic text-slate-600 hover:bg-slate-100 ${toolbarSize}`} title="Italic (em02)">
          <Italic className={iconSize} />
        </button>
        <button onClick={() => wrapSelection('__', '__')} className={`px-1 py-0.5 rounded underline text-slate-600 hover:bg-slate-100 ${toolbarSize}`} title="Underline (em03)">
          <Underline className={iconSize} />
        </button>
        <span className="w-px h-3.5 bg-slate-200 mx-0.5" />
        <button onClick={() => { setShowRefInput(showRefInput === 'dmRef' ? null : 'dmRef'); setRefDisplay(''); setRefCode(''); }}
          className={`px-1 py-0.5 rounded font-mono transition-colors ${toolbarSize} ${showRefInput === 'dmRef' ? 'bg-violet-100 text-violet-700' : 'text-violet-600 hover:bg-violet-50'}`} title="Insert DM Reference">
          dmRef
        </button>
        <button onClick={() => { setShowRefInput(showRefInput === 'pmRef' ? null : 'pmRef'); setRefDisplay(''); setRefCode(''); }}
          className={`px-1 py-0.5 rounded font-mono transition-colors ${toolbarSize} ${showRefInput === 'pmRef' ? 'bg-blue-100 text-blue-700' : 'text-blue-600 hover:bg-blue-50'}`} title="Insert PM Reference">
          pmRef
        </button>
        <button onClick={() => { setShowRefInput(showRefInput === 'extRef' ? null : 'extRef'); setRefDisplay(''); setRefCode(''); }}
          className={`px-1 py-0.5 rounded font-mono transition-colors ${toolbarSize} ${showRefInput === 'extRef' ? 'bg-amber-100 text-amber-700' : 'text-amber-600 hover:bg-amber-50'}`} title="Insert External Pub Reference">
          extPubRef
        </button>
        <button onClick={() => { setShowRefInput(showRefInput === 'intRef' ? null : 'intRef'); setRefDisplay(''); setRefCode(''); setRefTargetType(''); }}
          className={`px-1 py-0.5 rounded font-mono transition-colors ${toolbarSize} ${showRefInput === 'intRef' ? 'bg-teal-100 text-teal-700' : 'text-teal-600 hover:bg-teal-50'}`} title="Insert Internal Reference (figure, table, para, step in this DM)">
          intRef
        </button>
      </div>

      {/* Reference insertion form */}
      {showRefInput && (
        <div className={`p-2.5 rounded-lg border space-y-2 ${showRefInput === 'dmRef' ? 'bg-violet-50 border-violet-200' : showRefInput === 'pmRef' ? 'bg-blue-50 border-blue-200' : showRefInput === 'intRef' ? 'bg-teal-50 border-teal-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-2">
            <Link2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <span className="text-xs font-semibold text-slate-600">Insert {showRefInput === 'dmRef' ? 'DM Reference' : showRefInput === 'pmRef' ? 'PM Reference' : showRefInput === 'intRef' ? 'Internal Reference' : 'External Pub Reference'}</span>
          </div>

          {showRefInput === 'intRef' && (
            <>
              {internalTargets.length > 0 ? (
                <Select onValueChange={(val) => {
                  const t = internalTargets[parseInt(val, 10)];
                  if (t) { setRefCode(t.id); setRefTargetType(t.targetType); }
                }}>
                  <SelectTrigger className="text-xs h-8 bg-white"><SelectValue placeholder="Select target in this data module..." /></SelectTrigger>
                  <SelectContent>
                    {internalTargets.map((t, ti) => (
                      <SelectItem key={ti} value={String(ti)}>
                        <span className="text-xs">{t.label}</span>
                        <span className="ml-2 font-mono text-[10px] text-slate-400">{t.id}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-[10px] text-slate-400 italic">No referenceable targets found in this DM. Enter an id manually.</p>
              )}
              <div className="flex items-center gap-2">
                <Input value={refCode} onChange={e => setRefCode(e.target.value)}
                  placeholder="Target id (e.g. fig-0001)"
                  className="text-xs h-7 flex-1 font-mono"
                  onKeyDown={e => { if (e.key === 'Enter') doInsertRef(); if (e.key === 'Escape') setShowRefInput(null); }}
                />
                <Select value={refTargetType} onValueChange={setRefTargetType}>
                  <SelectTrigger className="text-xs h-7 w-[180px] bg-white"><SelectValue placeholder="Target type..." /></SelectTrigger>
                  <SelectContent>
                    {irttOptions.map(o => (
                      <SelectItem key={o.value} value={o.value}>
                        <span className="font-mono text-[10px]">{o.value}</span>
                        <span className="ml-2 text-xs text-slate-500">{o.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {showRefInput === 'dmRef' && (() => {
            const seen = new Set();
            const dmOptions = [];
            for (const e of dmlEntries) {
              const dmc = e.dmc_formatted;
              if (dmc && !seen.has(dmc.toUpperCase())) {
                seen.add(dmc.toUpperCase());
                dmOptions.push({ value: dmc, label: `${dmc} — ${e.title || ''}` });
              }
            }
            for (const f of (dirFiles || [])) {
              const n = f.name.toUpperCase();
              if (n.startsWith('DMC-') && n.endsWith('.XML')) {
                const dmcName = f.name.replace(/\.xml$/i, '');
                if (!seen.has(dmcName.toUpperCase())) {
                  seen.add(dmcName.toUpperCase());
                  dmOptions.push({ value: dmcName, label: dmcName });
                }
              }
            }
            return dmOptions.length > 0 ? (
              <Select onValueChange={(val) => {
                const entry = dmlEntries.find(e => e.dmc_formatted === val);
                setRefDisplay(entry ? (entry.title || entry.dmc_formatted) : val);
                setRefCode(val);
              }}>
                <SelectTrigger className="text-xs h-8 bg-white"><SelectValue placeholder="Select Data Module..." /></SelectTrigger>
                <SelectContent>
                  {dmOptions.map((opt, oi) => (
                    <SelectItem key={oi} value={opt.value}>
                      <span className="font-mono text-[10px]">{opt.value}</span>
                      {opt.label !== opt.value && <span className="ml-2 text-xs text-slate-500">{opt.label.replace(opt.value + ' — ', '')}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-[10px] text-slate-400 italic">No data modules found.</p>
            );
          })()}

          {showRefInput === 'pmRef' && (() => {
            const pmOptions = [];
            const seen = new Set();
            for (const f of (dirFiles || [])) {
              const n = f.name.toUpperCase();
              if (n.startsWith('PMC-') && n.endsWith('.XML')) {
                const pmcName = f.name.replace(/\.xml$/i, '');
                if (!seen.has(pmcName.toUpperCase())) {
                  seen.add(pmcName.toUpperCase());
                  pmOptions.push({ value: f.name, label: pmcName });
                }
              }
            }
            return pmOptions.length > 0 ? (
              <Select onValueChange={(val) => { const clean = val.replace(/\.xml$/i, ''); setRefDisplay(clean); setRefCode(clean); }}>
                <SelectTrigger className="text-xs h-8 bg-white"><SelectValue placeholder="Select Publication Module..." /></SelectTrigger>
                <SelectContent>
                  {pmOptions.map((opt, oi) => (
                    <SelectItem key={oi} value={opt.value}><span className="font-mono text-[10px]">{opt.label}</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-[10px] text-slate-400 italic">No PMC files found.</p>
            );
          })()}

          {showRefInput !== 'intRef' && (
          <div className="flex items-center gap-2">
            <Input value={refDisplay} onChange={e => setRefDisplay(e.target.value)}
              placeholder={showRefInput === 'dmRef' ? 'Display name' : showRefInput === 'pmRef' ? 'Display name' : 'Title'}
              className="text-xs h-7 flex-1"
              onKeyDown={e => { if (e.key === 'Enter') doInsertRef(); if (e.key === 'Escape') setShowRefInput(null); }}
            />
            <Input value={refCode} onChange={e => setRefCode(e.target.value)}
              placeholder={showRefInput === 'dmRef' ? 'DMC code' : showRefInput === 'pmRef' ? 'PMC code' : 'Code (optional)'}
              className="text-xs h-7 w-[200px] font-mono"
              onKeyDown={e => { if (e.key === 'Enter') doInsertRef(); if (e.key === 'Escape') setShowRefInput(null); }}
            />
          </div>
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-6 text-xs gap-1" onClick={doInsertRef}><Plus className="w-3 h-3" /> Insert</Button>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setShowRefInput(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Textarea */}
      <Textarea ref={paraRef} value={value} onChange={handleChange} placeholder={placeholder} rows={rows}
        className={`text-sm resize-y min-h-[32px] ${compact ? 'text-xs' : ''}`}
        data-testid={`rpe-text-${id}`}
      />

      {/* Inline elements preview */}
      {inlineRefs && inlineRefs.length > 0 && (
        <div className="px-2.5 py-1.5 rounded bg-slate-50 border border-slate-100 text-sm leading-relaxed">
          <div className="flex items-center gap-1 mb-0.5">
            <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Preview</span>
            {inlineRefs.map((r, ri) => (
              <Badge key={ri} variant="outline" className="text-[8px] h-3.5 px-1">
                {r.type === 'emphasis' ? r.emphasisType : r.type}
              </Badge>
            ))}
          </div>
          <div className="text-slate-700 text-xs">{renderInlinePreview(value)}</div>
        </div>
      )}

      {/* Inline list */}
      {hasInlineList && (
        <div className="space-y-1.5 border-l-2 border-blue-200 pl-3">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">{listType}</Badge>
            <button onClick={removeInlineList} className="p-1 text-slate-300 hover:text-red-500" title="Remove list"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          {listItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2 group/item">
              <span className="text-xs text-slate-400 w-4 shrink-0">{listType === 'sequentialList' ? `${i + 1}.` : '\u2022'}</span>
              <Input value={item} onChange={e => updateListItem(i, e.target.value)} placeholder="List item..." className="text-sm h-7" />
              <button onClick={() => removeListItem(i)} className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover/item:opacity-100 transition-opacity"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          <Button variant="ghost" size="sm" className="text-xs text-blue-600 h-6 gap-1" onClick={addListItem}><Plus className="w-3 h-3" /> Add item</Button>
        </div>
      )}

      {/* Add list buttons (only when no list exists) */}
      {!hasInlineList && (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="text-[10px] h-5 gap-1 text-blue-500" onClick={() => addInlineList('randomList')}>
            <Plus className="w-2.5 h-2.5" /> randomList
          </Button>
          <Button variant="ghost" size="sm" className="text-[10px] h-5 gap-1 text-blue-500" onClick={() => addInlineList('sequentialList')}>
            <Plus className="w-2.5 h-2.5" /> sequentialList
          </Button>
        </div>
      )}
    </div>
  );
};
